import { ChangeDetectionStrategy, Component, computed, input, model } from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { S25RadioModule } from "../../../../s25-radio/s25.radio.module";

export type AdjustmentAllocation = { amount: number; type: "amount" | "percent"; reason: string };

export const emptyAllocation: AdjustmentAllocation = { amount: 0, type: "amount", reason: "" };

export function sameAllocation(first: AdjustmentAllocation, second: AdjustmentAllocation) {
    return first.amount === second.amount && first.type === second.type && first.reason === second.reason;
}

const REASON_LIMIT = 40;

@TypeManagerDecorator("s25-ng-adjustment-amount-field")
@Component({
    selector: "s25-ng-adjustment-amount-field",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25RadioModule],
    template: `
        <div class="type-row" role="radiogroup" [attr.aria-label]="'Adjustment type'">
            <s25-ng-radio
                [value]="allocation().type"
                [radioValue]="'amount'"
                [name]="groupName()"
                [disabled]="disabled()"
                (valueChange)="setType('amount')"
                >Amount</s25-ng-radio
            >
            <s25-ng-radio
                [value]="allocation().type"
                [radioValue]="'percent'"
                [name]="groupName()"
                [disabled]="disabled()"
                (valueChange)="setType('percent')"
                >Percent</s25-ng-radio
            >
        </div>

        <div class="amount-row">
            @if (allocation().type === "amount") {
                <span aria-hidden="true">$</span>
            }
            <input
                class="c-input"
                type="number"
                step="0.01"
                [attr.aria-label]="'Adjustment amount'"
                [disabled]="disabled()"
                [ngModel]="allocation().amount"
                (ngModelChange)="setAmount($event)"
            />
            @if (allocation().type === "percent") {
                <span aria-hidden="true">%</span>
            }
        </div>

        <label class="reason">
            <span class="reason-label">
                Reason
                <span class="reason-count" [class.over-limit]="overLimit()">
                    {{ allocation().reason.length }} / {{ reasonLimit }}
                </span>
            </span>
            <textarea
                class="cn-form__control"
                [maxlength]="reasonLimit"
                [disabled]="disabled()"
                [ngModel]="allocation().reason"
                (ngModelChange)="setReason($event)"
            ></textarea>
        </label>

        @if (applyFrom(); as source) {
            <button type="button" class="aw-button aw-button--outline" [disabled]="disabled()" (click)="apply(source)">
                {{ applyLabel() }}
            </button>
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 6px;
            max-width: 15em;
        }

        .type-row {
            display: flex;
            gap: 8px;
        }

        .amount-row {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .amount-row .c-input {
            width: min(10em, 100%);
        }

        .reason {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .reason-label {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            font-weight: 600;
        }

        .reason-count {
            font-weight: 400;
            color: var(--color--text-success);
        }

        .reason-count.over-limit {
            color: var(--color--text-danger);
        }

        :host([disabled="true"]) {
            opacity: 0.75;
        }
    `,
})
export class S25AdjustmentAmountFieldComponent {
    readonly allocation = model<AdjustmentAllocation>(emptyAllocation);
    readonly disabled = input(false);
    /** Distinguishes this field's radio pair from every other one on the page. */
    readonly groupName = input.required<string>();
    /** Shows a button that copies the given allocation over, eg from the invoice being left. */
    readonly applyFrom = input<AdjustmentAllocation | undefined>(undefined);
    readonly applyLabel = input("Apply adjustment");

    protected readonly reasonLimit = REASON_LIMIT;
    protected readonly overLimit = computed(() => this.allocation().reason.length >= REASON_LIMIT);

    protected setType(type: AdjustmentAllocation["type"]) {
        this.allocation.update((prev) => ({ ...prev, type }));
    }

    protected setAmount(amount: number) {
        this.allocation.update((prev) => ({ ...prev, amount: Number(amount) || 0 }));
    }

    protected setReason(reason: string) {
        this.allocation.update((prev) => ({ ...prev, reason }));
    }

    protected apply(source: AdjustmentAllocation) {
        this.allocation.set({ ...source });
    }
}
